/*──────────────────────────────────────────────────────────────
 * src/components/PrintSection.jsx — regenerated 2025-07-06
 * • Items with UseStartStop but **no Stop** now yield **0 hrs & $0**.
 * • Qty/Hrs column always shows correct hours; 0.00 if no Stop.
 * • Header/label now shows **“Qty”** even for UseStartStop items.
 * • Grid table **hides Start/Stop columns** when no UseStartStop items.
 * • Lease/Well shows blank Well when it doesn’t exist.
 * • Total Cost row auto-spans correct cols and remains bottom-right.
 *──────────────────────────────────────────────────────────────*/

import React, { useState } from "react";
import { animated } from "react-spring";
import Modal from "react-modal";

/*────────── helpers ──────────*/
const formatDate = (d) =>
  d
    ? new Date(`${d}T00:00:00`).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "N/A";

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : ""; // empty when no value so we can omit cell text

/* unified cost calculator */
const costFor = (item) => {
  const cost = parseFloat(item.Cost ?? item.ItemCost ?? 0);

  /* Start/Stop → hrs × cost (0 if no Stop) */
  if (item.UseStartStop === "Y") {
    if (!item.Start || !item.Stop) return 0;
    const hrs = (new Date(item.Stop) - new Date(item.Start)) / 3_600_000;
    return Math.max(hrs, 0) * cost;
  }

  /* Quantity × cost (blank/NaN ⇒ 1, “0.00” stays 0) */
  if (item.UseQuantity === "Y") {
    const qtyRaw = parseFloat(item.Quantity);
    const qty = Number.isNaN(qtyRaw) ? 1 : qtyRaw;
    return qty * cost;
  }

  return cost;
};

/* qty / hrs resolver */
const qtyValue = (it) => {
  if (it.UseStartStop === "Y") {
    if (!it.Start || !it.Stop) return "0.00";
    const hrs = (new Date(it.Stop) - new Date(it.Start)) / 3_600_000;
    return Math.max(hrs, 0).toFixed(2);
  }
  if (it.UseQuantity === "Y") {
    const qtyRaw = parseFloat(it.Quantity);
    const qty = Number.isNaN(qtyRaw) ? 1 : qtyRaw;
    return qty.toFixed(2);
  }
  return "";
};

const PrintSection = ({
  companyName,
  userRole,
  ticket,
  theme,
  buttonAnimation,
  isEditing,
  uploadedImages = [],
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [printSelections, setPrintSelections] = useState({
    ticketAndNote: true,
    image: true,
  });
  const [gridLevelView, setGridLevelView] = useState(false);

  const openPrintModal = () => setModalIsOpen(true);
  const closePrintModal = () => setModalIsOpen(false);
  const handleCheckboxChange = (e) =>
    setPrintSelections((s) => ({ ...s, [e.target.name]: e.target.checked }));

  const handlePrint = () => {
    const isSingle = ticket.Items.length <= 9 && uploadedImages.length <= 1;
    const netCost = ticket.Items.reduce((s, it) => s + costFor(it), 0).toFixed(
      2
    );

    /* flags */
    const hasStartStop = ticket.Items.some((it) => it.UseStartStop === "Y");

    /*──────── build rows ────────*/
    const buildRows = () =>
      ticket.Items.map((it, idx) => {
        const total = costFor(it).toFixed(2);
        const qty = qtyValue(it);
        const start = hasStartStop ? formatDateTime(it.Start) : "";
        const stop = hasStartStop ? formatDateTime(it.Stop) : "";
        return `
          <tr class="${idx % 2 ? "alt" : ""}">
            <td>${it.ItemDescription}</td>
            <td style="text-align:center">${qty}</td>
            ${hasStartStop ? `<td>${start}</td><td>${stop}</td>` : ""}
            <td style="text-align:right">${total}</td>
          </tr>`;
      }).join("");

    /*──────── assemble items markup ────────*/
    const itemsHTML = gridLevelView
      ? (() => {
          const header = `
            <tr>
              <th>Item</th>
              <th>Qty</th>
              ${hasStartStop ? "<th>Start</th><th>Stop</th>" : ""}
              <th>Total ($)</th>
            </tr>`;
          const totalRow = `
            <tr class="total-row">
              <td colspan="${
                hasStartStop ? 4 : 2
              }" style="text-align:right;font-weight:bold">Total Cost:</td>
              <td style="text-align:right;font-weight:bold">$${netCost}</td>
            </tr>`;
          return `
            <table class="xl-table">
              <thead>${header}</thead>
              <tbody>
                ${buildRows()}
                ${totalRow}
              </tbody>
            </table>`;
        })()
      : (() => {
          const cardItems = ticket.Items.map((it) => {
            const total = costFor(it).toFixed(2);
            const qty = qtyValue(it);
            return `
                <div class="item">
                  <h4>${it.ItemDescription}</h4>
                  ${qty ? `<p>Qty: ${qty}</p>` : ""}
                  ${
                    hasStartStop && it.Start && it.Stop
                      ? `<p>Start: ${formatDateTime(
                          it.Start
                        )}</p><p>Stop: ${formatDateTime(it.Stop)}</p>`
                      : ""
                  }
                  <p>Total: $${total}</p>
                </div>`;
          }).join("");
          return `
            <div class="items">${cardItems}
              <div style="text-align:right;font-weight:bold;margin-top:16px">Total Cost: $${netCost}</div>
            </div>`;
        })();

    /*──────── printable doc ─────────*/
    const html = `<!DOCTYPE html><html><head><title>Print</title>
      <style>
        body,html{margin:0;padding:0;width:100%;font-family:Arial,sans-serif;background:#f3f4f6;color:#333}
        .content{max-width:900px;margin:20px auto;padding:24px;background:#fff;border-radius:12px;box-shadow:0 3px 10px rgba(0,0,0,.12)}
        .section{margin-bottom:24px}
        .section h2{font-size:20px;margin-bottom:12px;color:#4a90e2;border-bottom:1px solid #ddd;padding-bottom:10px}
        .items{display:grid;grid-template-columns:repeat(auto-fill,minmax(${
          isSingle ? 180 : 220
        }px,1fr));gap:${isSingle ? "10px" : "16px"}}
        .item{background:#fafafa;padding:${
          isSingle ? "10px" : "16px"
        };border-radius:10px;box-shadow:0 1px 4px rgba(0,0,0,.08);transition:transform .15s}
        .item:hover{transform:translateY(-2px)}
        .item h4{margin:0 0 6px;font-size:1rem}.item p{margin:3px 0;font-size:.85rem}
        .xl-table{width:100%;border-collapse:collapse;font-size:.82rem;margin-top:10px}
        .xl-table th,.xl-table td{border:1px solid #cdd3d8;padding:6px 8px}
        .xl-table th{background:#eef5ff;text-align:left;font-weight:600}
        .xl-table tbody tr.alt{background:#fbfcfe}
        .xl-table td:nth-child(2){text-align:center}.xl-table td:last-child{text-align:right}
        .total-row td{background:#eef5ff;font-weight:700}
        img{max-width:100%;max-height:360px;margin:20px auto;display:block;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.1);page-break-inside:avoid}
        .page-break{page-break-before:always}
        @media print{@page{size:A4;margin:10mm}body{-webkit-print-color-adjust:exact}}
      </style></head><body>
      <div class="content">
        ${
          printSelections.ticketAndNote
            ? `
          <div class="section">
            <h2>${companyName}</h2>
            <div><strong>Date:</strong> ${formatDate(ticket.TicketDate)}</div>
            <div><strong>Ticket #:</strong> ${ticket.Ticket || "N/A"}</div>
            <div><strong>Lease${ticket.WellID ? "/Well" : ""}:</strong> ${
                ticket.LeaseName || "N/A"
              }${ticket.WellID ? " / " + ticket.WellID : ""}</div>
            <div><strong>User:</strong> ${ticket.UserID || "N/A"}</div>
            <div><strong>Ticket Type:</strong> ${
              ticket.JobDescription || "N/A"
            }</div>
            ${itemsHTML}
            ${
              ticket.Note
                ? `<div style="margin-top:20px"><h2>Note</h2><div>${ticket.Note}</div></div>`
                : ""
            }
          </div>`
            : ""
        }
        ${
          printSelections.image && uploadedImages.length
            ? `
          ${!isSingle ? '<div class="page-break"></div>' : ""}
          <h2 style="text-align:center">${
            uploadedImages.length > 1 ? "Uploaded Images" : "Uploaded Image"
          }</h2>
          ${uploadedImages
            .map((img, i) => `<img src="${img}" alt="Img${i + 1}">`)
            .join("")}`
            : ""
        }
      </div>
    </body></html>`;

    const w = window.open("", "", "width=900,height=650");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.onload = () => w.print();
  };

  /*──────── component UI ────────*/
  return (
    <div className="flex items-center justify-center">
      <animated.div style={buttonAnimation}>
        {!isEditing && userRole !== "P" && (
          <button
            onClick={openPrintModal}
            className={`px-6 py-3 font-semibold rounded-lg shadow-lg transition transform hover:-translate-y-1 hover:scale-105 ${
              theme === "dark"
                ? "bg-blue-600 hover:bg-blue-700 text-gray-200"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            Print
          </button>
        )}
      </animated.div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closePrintModal}
        contentLabel="Print Options"
        className={`relative rounded-lg shadow-xl p-8 max-w-lg w-full ${
          theme === "dark"
            ? "bg-gray-800 text-gray-100"
            : "bg-white text-gray-900"
        }`}
        overlayClassName="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center"
      >
        <label className="absolute top-4 right-4 flex items-center space-x-2 text-xs select-none">
          <span>Grid-level view</span>
          <input
            type="checkbox"
            checked={gridLevelView}
            onChange={() => setGridLevelView(!gridLevelView)}
            className="form-checkbox h-4 w-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </label>

        <h2 className="text-2xl font-bold mb-6 text-center border-b pb-4">
          Print Options
        </h2>

        <form className="space-y-6">
          <label className="flex items-center space-x-4">
            <input
              type="checkbox"
              name="ticketAndNote"
              checked={printSelections.ticketAndNote}
              onChange={handleCheckboxChange}
              className={`form-checkbox h-5 w-5 rounded ${
                theme === "dark" ? "text-blue-400" : "text-blue-600"
              }`}
            />
            <span className="text-lg">Ticket</span>
          </label>

          <label className="flex items-center space-x-4">
            <input
              type="checkbox"
              name="image"
              checked={printSelections.image}
              onChange={handleCheckboxChange}
              className={`form-checkbox h-5 w-5 rounded ${
                theme === "dark" ? "text-blue-400" : "text-blue-600"
              }`}
            />
            <span className="text-lg">Image</span>
          </label>
        </form>

        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={handlePrint}
            className={`px-6 py-3 rounded-lg text-lg font-semibold transform hover:scale-105 shadow-md ${
              theme === "dark"
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Print
          </button>

          <button
            onClick={closePrintModal}
            className={`px-6 py-3 rounded-lg text-lg font-semibold transform hover:scale-105 shadow-md ${
              theme === "dark"
                ? "bg-gray-600 text-white hover:bg-gray-700"
                : "bg-gray-300 text-gray-800 hover:bg-gray-400"
            }`}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PrintSection;
